import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import {
  FiPlay,
  FiPause,
  FiVolume2,
  FiVolumeX,
  FiMonitor,
  FiAlertCircle,
} from 'react-icons/fi';
import { loadYouTubeIframeAPI } from '../utils/youtubeIframeApi';
import { getYouTubeThumbnailUrl } from '../utils/urlParser';

const mapYtError = (code) => {
  if (code === 2) return 'Invalid playback request';
  if (code === 5) return 'Playback not supported in this browser';
  if (code === 100) return 'Video unavailable or removed';
  if (code === 101 || code === 150) return 'Embedding disabled for this video';
  return 'Playback error';
};

const ControlBtn = ({ label, onClick, disabled, active, children, theme, accent }) => (
  <Box
    as="button"
    type="button"
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
    display="flex"
    alignItems="center"
    justifyContent="center"
    w="42px"
    h="42px"
    borderRadius="xl"
    cursor={disabled ? 'not-allowed' : 'pointer'}
    opacity={disabled ? 0.45 : 1}
    transition="transform 0.15s ease, background 0.2s ease, box-shadow 0.2s ease"
    style={{
      background: active ? `${accent}28` : 'rgba(255,255,255,0.06)',
      border: `1px solid ${active ? `${accent}55` : 'rgba(255,255,255,0.1)'}`,
      color: active ? accent : theme.textPrimary,
      boxShadow: active ? `0 0 20px ${accent}22` : 'none',
    }}
    _hover={!disabled ? { transform: 'scale(1.04)', background: 'rgba(255,255,255,0.1)' } : {}}
    _active={!disabled ? { transform: 'scale(0.97)' } : {}}
  >
    {children}
  </Box>
);

/**
 * @param {{
 *   videoId: string | null,
 *   listId: string | null,
 *   theme: object,
 *   accent?: string,
 *   volume: number,
 *   muted: boolean,
 *   onVolumeChange: (n: number) => void,
 *   onMutedChange: (b: boolean) => void,
 *   onSwitchToStandard: () => void,
 * }} props
 */
const YouTubeAudioDock = ({
  videoId,
  listId,
  theme,
  accent = '#e53935',
  volume,
  muted,
  onVolumeChange,
  onMutedChange,
  onSwitchToStandard,
}) => {
  const mountRef = useRef(null);
  const playerRef = useRef(null);
  const volumeRef = useRef(volume);
  const mutedRef = useRef(muted);
  const [apiStatus, setApiStatus] = useState('loading'); // loading | ready | error
  const [playerState, setPlayerState] = useState(-1);
  const [title, setTitle] = useState('');
  const [errorText, setErrorText] = useState('');

  const thumbUrl = getYouTubeThumbnailUrl(videoId);

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);
  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  const destroyPlayer = useCallback(() => {
    try {
      playerRef.current?.destroy?.();
    } catch {
      /* ignore */
    }
    playerRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    setApiStatus('loading');
    setErrorText('');
    setPlayerState(-1);
    setTitle('');
    destroyPlayer();

    const hasVideo = Boolean(videoId);
    const hasList = Boolean(listId);
    if (!hasVideo && !hasList) {
      setApiStatus('error');
      setErrorText('No playable ID in this link');
      return undefined;
    }

    loadYouTubeIframeAPI()
      .then(() => {
        if (cancelled || !mountRef.current) return;

        const playerVars = {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          enablejsapi: 1,
          origin: window.location.origin,
        };

        if (hasList) {
          playerVars.list = listId;
          playerVars.listType = 'playlist';
        }

        const cfg = {
          width: '200',
          height: '200',
          playerVars,
          events: {
            onReady: (e) => {
              if (cancelled) return;
              setApiStatus('ready');
              try {
                e.target.setVolume(volumeRef.current);
                if (mutedRef.current) e.target.mute();
                else e.target.unMute();
              } catch {
                /* ignore */
              }
              try {
                const d = e.target.getVideoData?.();
                if (d?.title) setTitle(d.title);
              } catch {
                /* ignore */
              }
            },
            onStateChange: (e) => {
              if (cancelled) return;
              setPlayerState(e.data);
              try {
                const d = e.target.getVideoData?.();
                if (d?.title) setTitle(d.title);
              } catch {
                /* ignore */
              }
            },
            onError: (e) => {
              if (cancelled) return;
              setErrorText(mapYtError(e.data));
              setApiStatus('error');
            },
          },
        };

        if (hasVideo) {
          cfg.videoId = videoId;
        }

        try {
          const YT = window.YT;
          if (!YT?.Player) {
            setErrorText('YouTube API unavailable');
            setApiStatus('error');
            return;
          }
          playerRef.current = new YT.Player(mountRef.current, cfg);
        } catch {
          setErrorText('Could not start player');
          setApiStatus('error');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setApiStatus('error');
          setErrorText('Could not load YouTube API');
        }
      });

    return () => {
      cancelled = true;
      destroyPlayer();
    };
  }, [videoId, listId, destroyPlayer]);

  useEffect(() => {
    const p = playerRef.current;
    if (!p?.setVolume || apiStatus !== 'ready') return;
    try {
      p.setVolume(volume);
    } catch {
      /* ignore */
    }
  }, [volume, apiStatus]);

  useEffect(() => {
    const p = playerRef.current;
    if (!p?.mute || apiStatus !== 'ready') return;
    try {
      if (muted) p.mute();
      else p.unMute();
    } catch {
      /* ignore */
    }
  }, [muted, apiStatus]);

  const togglePlay = () => {
    const p = playerRef.current;
    if (!p?.playVideo) return;
    try {
      if (playerState === 1) p.pauseVideo();
      else p.playVideo();
    } catch {
      /* ignore */
    }
  };

  const toggleMute = () => {
    onMutedChange(!muted);
  };

  const isPlaying = playerState === 1;
  const controlsDisabled = apiStatus !== 'ready' || Boolean(errorText);

  return (
    <Box position="relative" borderRadius="2xl" overflow="hidden">
      {/* Hidden functional mount — keeps playback reliable; not the visual focus */}
      <Box
        className="fl-yt-api-mount-wrap"
        position="absolute"
        bottom={0}
        right={0}
        w="4px"
        h="4px"
        overflow="hidden"
        opacity={0}
        pointerEvents="none"
        aria-hidden
        zIndex={0}
      >
        <Box ref={mountRef} className="fl-yt-api-mount-inner" />
      </Box>

      {/* Art / atmosphere */}
      <Box position="relative" minH="140px" borderRadius="2xl" overflow="hidden">
        {thumbUrl ? (
          <>
            <Box
              position="absolute"
              inset={0}
              backgroundImage={`url(${thumbUrl})`}
              backgroundSize="cover"
              backgroundPosition="center"
              transform="scale(1.08)"
              filter="blur(28px) saturate(1.1)"
              opacity={0.55}
            />
            <Box
              position="absolute"
              inset={0}
              style={{
                background: `linear-gradient(165deg, rgba(8,6,16,0.5) 0%, rgba(6,4,14,0.92) 55%, rgba(4,2,12,0.97) 100%)`,
              }}
            />
          </>
        ) : (
          <Box
            position="absolute"
            inset={0}
            style={{
              background: `linear-gradient(145deg, ${accent}22 0%, rgba(12,10,24,0.95) 45%, rgba(6,4,14,1) 100%)`,
            }}
          />
        )}

        <Box position="relative" zIndex={1} p={4} pb={3}>
          <Text
            fontSize="sm"
            fontWeight="600"
            lineHeight="short"
            textAlign="center"
            noOfLines={3}
            style={{ color: theme.textPrimary }}
          >
            {title || (listId && !videoId ? 'Playlist' : 'Now playing')}
          </Text>

          {errorText && (
            <Flex align="center" gap={2} mt={3} p={2} borderRadius="lg" style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)' }}>
              <FiAlertCircle size={14} color="#f87171" />
              <Text fontSize="11px" style={{ color: '#fca5a5' }}>{errorText}</Text>
            </Flex>
          )}

        </Box>
      </Box>

      {/* Custom controls */}
      <Box
        px={4}
        pb={4}
        pt={1}
        style={{
          borderTop: `1px solid ${theme.cardBorder}`,
          background: 'rgba(0,0,0,0.25)',
        }}
      >
        <Flex align="center" justify="center" gap={3} mb={4}>
          <ControlBtn
            label={isPlaying ? 'Pause' : 'Play'}
            onClick={togglePlay}
            disabled={controlsDisabled}
            active={isPlaying}
            theme={theme}
            accent={accent}
          >
            {isPlaying ? <FiPause size={20} /> : <FiPlay size={20} style={{ marginLeft: 2 }} />}
          </ControlBtn>
          <ControlBtn
            label={muted ? 'Unmute' : 'Mute'}
            onClick={toggleMute}
            disabled={controlsDisabled}
            active={muted}
            theme={theme}
            accent={accent}
          >
            {muted ? <FiVolumeX size={18} /> : <FiVolume2 size={18} />}
          </ControlBtn>
          <ControlBtn
            label="Show standard video player"
            onClick={onSwitchToStandard}
            disabled={false}
            active={false}
            theme={theme}
            accent={accent}
          >
            <FiMonitor size={18} />
          </ControlBtn>
        </Flex>

        <Flex align="center" gap={3}>
          <Text fontSize="9px" fontWeight="600" letterSpacing="0.08em" textTransform="uppercase" style={{ color: theme.textDim, minWidth: '52px' }}>
            Volume
          </Text>
          <input
            type="range"
            min={0}
            max={100}
            value={volume}
            onChange={(e) => onVolumeChange(Number(e.target.value))}
            disabled={controlsDisabled}
            className="fl-slider"
            aria-label="Volume"
            style={{ flex: 1, '--fl-accent': accent, opacity: controlsDisabled ? 0.4 : 1 }}
          />
          <Text className="fl-mono" fontSize="11px" style={{ color: theme.textMuted, minWidth: '28px', textAlign: 'right' }}>
            {volume}
          </Text>
        </Flex>
      </Box>
    </Box>
  );
};

export default YouTubeAudioDock;
